import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, alpha } from '@mui/material';
import { Star } from '@mui/icons-material';
import { trackProviderCardClick } from '../../services/analyticsService';
import { getThumbOrOriginal, markThumbFailed } from '../../utils/thumbImage';
import { getCropImageStyle } from '../../utils/imageCrop';
import { primaryCounty } from '../../constants/counties';

const DEFAULT_IMAGE = '/images/default-provider.jpg';
const NATIONWIDE = 'Magyarország teljes területe';
const THUMB_SIZE = 60;

// A FeaturedBadge-dzsel egyező szintnevek, kis címkeként
const TIER_LABELS = { TOP: 'TOP', TIER2: 'Kiemelt', TIER3: 'Ajánlott' };

const locationLabel = (countyField) => {
  if ((countyField || '').includes(NATIONWIDE)) return 'Országosan';
  return primaryCounty(countyField);
};

/**
 * Kompakt szolgáltató-sor (főoldali kategória-panelek): kis kép, név, értékelés, helyszín.
 * Fix magasságú, könnyű elem — nincs galéria, animáció, kedvenc gomb — így minden méretben egyforma.
 * A data-provider-id-t a főoldali megjelenés-mérés (HomeDiscoveryBands) használja.
 */
const ProviderMiniRow = ({ provider, listName, listPosition }) => {
  const [, setThumbFailures] = useState(0);
  const original = provider.images?.[0]?.imageUrl || provider.user?.avatarUrl || DEFAULT_IMAGE;
  const src = original === DEFAULT_IMAGE ? original : getThumbOrOriginal(original);
  const rating = parseFloat(provider.ratingAverage) || 0;
  const location = locationLabel(provider.county);
  const tierLabel = provider.featured ? TIER_LABELS[provider.featured.tier] : null;

  return (
    <Box
      component={Link}
      to={`/szolgaltato/${provider.slug || provider.id}`}
      data-provider-id={provider.id}
      onClick={() => trackProviderCardClick(provider.slug || provider.id, provider.category?.name, listPosition, listName)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        minHeight: THUMB_SIZE + 16,
        borderRadius: '12px',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'background-color 0.2s ease',
        '@media (hover: hover)': { '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.05) } },
        '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 1 },
      }}
    >
      <Box sx={{ position: 'relative', width: THUMB_SIZE, height: THUMB_SIZE, flexShrink: 0, borderRadius: '10px', overflow: 'hidden', bgcolor: 'grey.100', containerType: 'size' }}>
        <img
          src={src}
          alt=""
          width={THUMB_SIZE}
          height={THUMB_SIZE}
          loading="lazy"
          decoding="async"
          onError={() => {
            // Hiányzó bélyegkép: közös cache-be, és az eredetivel próbáljuk újra
            if (src !== original) {
              markThumbFailed(original);
              setThumbFailures((n) => n + 1);
            }
          }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...getCropImageStyle(provider.images?.[0]) }}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Typography noWrap component="span" sx={{ fontWeight: 600, color: 'primary.dark', fontSize: '0.95rem', minWidth: 0 }}>
            {provider.businessName}
          </Typography>
          {tierLabel && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                px: 0.75,
                borderRadius: '6px',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: 0.3,
                lineHeight: '18px',
                textTransform: 'uppercase',
                // A FeaturedBadge TOP-jelvényével egyező arany háttér, sötét szöveg
                // (a téma secondary.dark színe világos krém, azon nem olvasható)
                color: 'text.primary',
                bgcolor: 'secondary.main',
              }}
            >
              {tierLabel}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary', fontSize: '0.8rem', minWidth: 0 }}>
          {rating > 0 ? (
            <>
              <Star sx={{ fontSize: 15, color: 'secondary.main' }} />
              <span>{rating.toFixed(1).replace('.', ',')}</span>
              <span>({provider.reviewCount || 0})</span>
            </>
          ) : (
            <span>Még nincs értékelés</span>
          )}
          {location && (
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              · {location}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProviderMiniRow;
